type PendingRequest<T> = {
  resolve: (value: T) => void;
  reject: (error: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
};

type WorkerInstance = {
  worker: Worker;
  isReady: boolean;
  isBusy: boolean;
  pendingRequests: Map<string, PendingRequest<unknown>>;
  readyPromise: Promise<void>;
  readyResolve: () => void;
};

type WorkerPool = {
  workers: WorkerInstance[];
  queue: QueuedTask[];
};

type QueuedTask = {
  type: string;
  payload: unknown;
  timeout: number;
  resolve: (value: unknown) => void;
  reject: (error: Error) => void;
};

const DEFAULT_TIMEOUT = 30000;

const POOL_CONFIGS: Record<string, { poolSize: number }> = {
  'http-parser': { poolSize: 2 },
  'body-processor': { poolSize: 4 },
  search: { poolSize: 1 },
};

class WorkerManager {
  private pools: Map<string, WorkerPool> = new Map();
  private idCounter = 0;

  private createWorkerInstance(name: string, index = 0): WorkerInstance {
    let worker: Worker;
    const workerName = index > 0 ? `${name}-${index}` : name;

    switch (name) {
      case 'http-parser':
        worker = new Worker(
          new URL('./http-parser.worker.ts', import.meta.url),
          { name: workerName },
        );
        break;
      case 'body-processor':
        worker = new Worker(
          new URL('./body-processor.worker.ts', import.meta.url),
          { name: workerName },
        );
        break;
      case 'search':
        worker = new Worker(new URL('./search.worker.ts', import.meta.url), {
          name: workerName,
        });
        break;
      default:
        throw new Error(`Unknown worker: ${name}`);
    }

    let readyResolve: () => void = () => {
      /* will be set */
    };
    const readyPromise = new Promise<void>((resolve) => {
      readyResolve = resolve;
    });

    const instance: WorkerInstance = {
      worker,
      isReady: false,
      isBusy: false,
      pendingRequests: new Map(),
      readyPromise,
      readyResolve,
    };

    worker.onmessage = (event) => {
      const data = event.data;

      if (data.type === 'ready') {
        instance.isReady = true;
        instance.readyResolve();
        return;
      }

      const pending = instance.pendingRequests.get(data.id);
      if (pending) {
        clearTimeout(pending.timeoutId);
        instance.pendingRequests.delete(data.id);
        instance.isBusy = instance.pendingRequests.size > 0;

        if (data.type === 'error') {
          pending.reject(new Error(data.error));
        } else {
          pending.resolve(data.result);
        }

        this.processQueue(name);
      }
    };

    worker.onerror = (error) => {
      console.error(`Worker ${name} error:`, error);

      for (const [, pending] of instance.pendingRequests) {
        clearTimeout(pending.timeoutId);
        pending.reject(new Error(`Worker error: ${error.message}`));
      }
      instance.pendingRequests.clear();
      instance.isBusy = false;

      this.processQueue(name);
    };

    return instance;
  }

  private getOrCreatePool(name: string): WorkerPool {
    const existing = this.pools.get(name);
    if (existing) {
      return existing;
    }

    const config = POOL_CONFIGS[name] || { poolSize: 1 };
    const workers: WorkerInstance[] = [];

    for (let i = 0; i < config.poolSize; i++) {
      workers.push(this.createWorkerInstance(name, i));
    }

    const pool: WorkerPool = {
      workers,
      queue: [],
    };

    this.pools.set(name, pool);
    return pool;
  }

  private getAvailableWorker(pool: WorkerPool): WorkerInstance | null {
    for (const worker of pool.workers) {
      if (worker.isReady && !worker.isBusy) {
        return worker;
      }
    }
    return null;
  }

  private processQueue(name: string): void {
    const pool = this.pools.get(name);
    if (!pool || pool.queue.length === 0) {
      return;
    }

    const availableWorker = this.getAvailableWorker(pool);
    if (!availableWorker) {
      return;
    }

    const task = pool.queue.shift();
    if (!task) {
      return;
    }

    this.executeOnWorker(
      availableWorker,
      name,
      task.type,
      task.payload,
      task.timeout,
    )
      .then(task.resolve)
      .catch(task.reject);
  }

  private async executeOnWorker<TResult>(
    instance: WorkerInstance,
    workerName: string,
    type: string,
    payload: unknown,
    timeout: number,
  ): Promise<TResult> {
    await instance.readyPromise;

    const id = this.generateId();
    instance.isBusy = true;

    return new Promise<TResult>((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        instance.pendingRequests.delete(id);
        instance.isBusy = instance.pendingRequests.size > 0;
        reject(
          new Error(
            `Worker ${workerName} request timed out after ${timeout}ms`,
          ),
        );
        this.processQueue(workerName);
      }, timeout);

      instance.pendingRequests.set(id, {
        resolve: resolve as (value: unknown) => void,
        reject,
        timeoutId,
      });

      instance.worker.postMessage({
        type,
        id,
        payload,
      });
    });
  }

  private generateId(): string {
    return `req_${Date.now()}_${++this.idCounter}`;
  }

  async sendMessage<TRequest, TResult>(
    workerName: string,
    type: string,
    payload: TRequest,
    timeout: number = DEFAULT_TIMEOUT,
  ): Promise<TResult> {
    const pool = this.getOrCreatePool(workerName);
    const availableWorker = this.getAvailableWorker(pool);

    if (availableWorker) {
      return this.executeOnWorker<TResult>(
        availableWorker,
        workerName,
        type,
        payload,
        timeout,
      );
    }

    return new Promise<TResult>((resolve, reject) => {
      pool.queue.push({
        type,
        payload,
        timeout,
        resolve: resolve as (value: unknown) => void,
        reject,
      });
    });
  }

  terminateWorker(name: string): void {
    const pool = this.pools.get(name);
    if (pool) {
      for (const instance of pool.workers) {
        for (const [, pending] of instance.pendingRequests) {
          clearTimeout(pending.timeoutId);
          pending.reject(new Error('Worker terminated'));
        }
        instance.worker.terminate();
      }

      for (const task of pool.queue) {
        task.reject(new Error('Worker terminated'));
      }

      this.pools.delete(name);
    }
  }

  terminateAll(): void {
    for (const name of this.pools.keys()) {
      this.terminateWorker(name);
    }
  }

  isWorkerReady(name: string): boolean {
    const pool = this.pools.get(name);
    if (!pool) return false;
    return pool.workers.some((w) => w.isReady);
  }

  async warmup(workerNames: string[]): Promise<void> {
    const promises: Promise<void>[] = [];

    for (const name of workerNames) {
      const pool = this.getOrCreatePool(name);
      for (const worker of pool.workers) {
        promises.push(worker.readyPromise);
      }
    }

    await Promise.all(promises);
  }

  getPoolStats(
    name: string,
  ): { total: number; ready: number; busy: number; queued: number } | null {
    const pool = this.pools.get(name);
    if (!pool) return null;

    return {
      total: pool.workers.length,
      ready: pool.workers.filter((w) => w.isReady).length,
      busy: pool.workers.filter((w) => w.isBusy).length,
      queued: pool.queue.length,
    };
  }
}

let workerManager: WorkerManager | null = null;

export function getWorkerManager(): WorkerManager | null {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!workerManager) {
    workerManager = new WorkerManager();
  }

  return workerManager;
}

export function areWorkersSupported(): boolean {
  return typeof window !== 'undefined' && typeof Worker !== 'undefined';
}

export type { WorkerManager };
